import isFunction from 'lodash/isFunction';
import { bugsnagNotifyWrapper } from 'containers/ErrorBoundary/utils';

/**
 * Executes a sequence of functions or thunks in order, with error handling.
 *
 * Functions are executed sequentially. If a function returns another function (thunk),
 * it will be dispatched through Redux. Stops execution on first error unless handled.
 *
 * @param {Array<Array>} config - Array of [function, arguments] pairs to execute sequentially
 * @param {Function} [onCatchError] - Optional error handler function. If not provided, uses bugsnagNotifyWrapper
 * @returns {Function} Redux thunk function
 *
 * @example
 * // Basic usage with API calls and thunks
 * dispatch(sequence([
 *   [hubScenesCreate, [serial, rule]],           // API call
 *   [EzloActions.refreshSceneList, [serial]],    // Redux thunk
 * ]));
 *
 * @example
 * // With custom error handler
 * dispatch(sequence([
 *   [hubScenesCreate, [serial, rule]],
 *   [EzloActions.offRule, [serial, meshBotId]],
 * ], (error) => {
 *   console.error('Sequence failed:', error);
 *   toast(error.message, { type: TOAST_TYPE.ERROR });
 * }));
 *
 */
export const sequence = (config, onCatchError) => async (dispatch) => {
    try {
        for (const [fn, args] of config) {
            const result = await fn(...args);
            if (isFunction(result)) {
                await dispatch(result);
            }
        }
    } catch (e) {
        if (isFunction(onCatchError)) {
            onCatchError(e);
        } else {
            bugsnagNotifyWrapper(e, { config });
        }
    }
};
